/**
 * Invite status and application hooks.
 *
 * The whole application flow is expressed as one server-side status object: the
 * client never combines a cooldown, a decision and a delivery timestamp into a
 * state of its own, it renders whatever `GET /api/invite/status` reports. Every
 * mutation answers with the same object, so a successful action is a cache write
 * instead of a second round trip.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { Locale } from '@shared/locales'
import type { InviteStatusResponse } from '@shared/types'

import { apiRequest } from '../lib/api'
import { currentLocale } from '../lib/i18n'
import { queryKeys } from '../lib/query'
import { selectIsAuthenticated, useSessionStore } from '../stores/session'

export function useInviteStatus() {
  const isAuthenticated = useSessionStore(selectIsAuthenticated)

  return useQuery({
    queryKey: queryKeys.inviteStatus,
    enabled: isAuthenticated,
    queryFn: () =>
      apiRequest<InviteStatusResponse>('/api/invite/status', { locale: currentLocale() }),
  })
}

/**
 * Shared plumbing for the four invite mutations.
 *
 * `refreshUser` is set only by redemption: it is the one action that changes the
 * account itself (`isInvited` and the activation timestamp), and the header and
 * the create-event gate read those from the account cache.
 */
function useInviteStatusMutation<TInput>(
  request: (input: TInput, locale: Locale) => Promise<InviteStatusResponse>,
  refreshUser = false,
) {
  const queryClient = useQueryClient()

  return useMutation({
    // The locale is read when the action runs rather than when the hook renders:
    // the server uses it for the message it returns, and the language may have
    // changed in between.
    mutationFn: (input: TInput) => request(input, currentLocale()),
    onSuccess: (status) => {
      queryClient.setQueryData(queryKeys.inviteStatus, status)
      if (refreshUser) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.me })
      }
    },
  })
}

/** Files the caller's request for an invite. */
export function useApplyInvite() {
  return useInviteStatusMutation<{ reason: string | null }>((input, locale) =>
    apiRequest<InviteStatusResponse>('/api/invite/apply', {
      method: 'POST',
      body: { reason: input.reason },
      locale,
    }),
  )
}

/** Withdraws the caller's own pending request. */
export function useWithdrawInvite() {
  return useInviteStatusMutation<void>((_input, locale) =>
    apiRequest<InviteStatusResponse>('/api/invite/apply/withdraw', { method: 'POST', locale }),
  )
}

/** Redeems an invite code against the signed-in account. */
export function useRedeemInvite() {
  return useInviteStatusMutation<{ code: string }>(
    (input, locale) =>
      apiRequest<InviteStatusResponse>('/api/invite/redeem', {
        method: 'POST',
        body: { code: input.code },
        locale,
      }),
    true,
  )
}

/** Sends the bound invite code mail again without issuing a new code. */
export function useResendInvite() {
  return useInviteStatusMutation<void>((_input, locale) =>
    apiRequest<InviteStatusResponse>('/api/invite/resend', { method: 'POST', locale }),
  )
}
